import React, { useEffect, useState, useMemo, useCallback } from 'react';
import type { Step } from 'react-joyride';
import Joyride from 'react-joyride';
import {
  DEFAULT_OVERLAY_COLOR,
  DEFAULT_SPOTLIGHT_PADDING,
  DEFAULT_SPOTLIGHT_RADIUS,
  HIDDEN_JOYRIDE_STYLES,
  POPOVER_SELECTORS,
  TOUR_OVERLAY_Z_INDEX,
} from '../constants';

interface TourOverlayProps {
  shouldShowOverlay: boolean;
  target: string | HTMLElement | null;
  children?: React.ReactNode;
  overlayColor?: string | undefined;
  spotlightPadding?: number | undefined;
  spotlightRadius?: number | undefined;
  onOverlayClick?: () => void;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({
  shouldShowOverlay,
  target,
  children,
  overlayColor = DEFAULT_OVERLAY_COLOR,
  spotlightPadding = DEFAULT_SPOTLIGHT_PADDING,
  spotlightRadius = DEFAULT_SPOTLIGHT_RADIUS,
  onOverlayClick,
}) => {
  const [steps, setSteps] = useState<Step[]>([]);

  const targetElement = useMemo(() => {
    if (!target) return null;
    return typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target;
  }, [target]);

  const handleDocumentInteraction = useCallback(
    (e: Event) => {
      if (!onOverlayClick || !targetElement) return;

      const eventTarget = e.target;

      // Ensure we have a valid element
      if (!eventTarget || !(eventTarget instanceof Element)) {
        return;
      }

      // Allow interactions on the target element
      if (targetElement.contains(eventTarget)) {
        return;
      }

      // Allow interactions on popovers
      const isOnPopover = POPOVER_SELECTORS.some((selector) => {
        try {
          return eventTarget.closest(selector) !== null;
        } catch {
          return false;
        }
      });

      if (isOnPopover) {
        return;
      }

      // Block all other interactions
      e.preventDefault();
      e.stopPropagation();

      if (e.type === 'click') {
        onOverlayClick();
      }
    },
    [onOverlayClick, targetElement],
  );

  useEffect(() => {
    if (!shouldShowOverlay || !onOverlayClick) return;

    const events = [
      'click',
      'mousedown',
      'mouseup',
      'mouseenter',
      'mouseover',
      'mouseleave',
      'mouseout',
      'focus',
      'focusin',
      'focusout',
      'blur',
      'pointerdown',
      'pointerup',
      'pointerenter',
      'pointerover',
      'pointerleave',
      'touchstart',
      'touchend',
      'keydown',
      'keyup',
    ];

    events.forEach((eventType) => {
      document.addEventListener(eventType, handleDocumentInteraction, {
        capture: true,
        passive: false,
      });
    });

    return () => {
      events.forEach((eventType) => {
        document.removeEventListener(
          eventType,
          handleDocumentInteraction,
          true,
        );
      });
    };
  }, [shouldShowOverlay, onOverlayClick, handleDocumentInteraction]);

  const joyrideSteps = useMemo<Step[]>(() => {
    if (!shouldShowOverlay || !target) return [];

    return [
      {
        target,
        content: '',
        disableBeacon: true,
        hideCloseButton: true,
        hideFooter: true,
        spotlightPadding,
        styles: {
          ...HIDDEN_JOYRIDE_STYLES,
          overlay: {
            backgroundColor: overlayColor,
            pointerEvents: 'none', // Let Joyride overlay be non-blocking
          },
          spotlight: {
            borderRadius: spotlightRadius,
          },
        },
      },
    ];
  }, [
    shouldShowOverlay,
    target,
    overlayColor,
    spotlightPadding,
    spotlightRadius,
  ]);
  useEffect(() => {
    setSteps(joyrideSteps);
  }, [joyrideSteps]);

  return (
    <>
      <Joyride
        steps={steps}
        run={shouldShowOverlay && steps.length > 0}
        continuous={false}
        showProgress={false}
        showSkipButton={false}
        disableOverlayClose={true}
        disableScrollParentFix={true}
        disableCloseOnEsc={true}
        spotlightClicks={true}
        hideCloseButton={true}
        hideBackButton={true}
        styles={{
          options: {
            zIndex: TOUR_OVERLAY_Z_INDEX,
            arrowColor: 'transparent',
          },
        }}
        callback={() => {}}
      />
      {shouldShowOverlay && children}
    </>
  );
};
